import React from 'react';
import styled from 'styled-components';

import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {SearchLocalFilterIconType} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {renderLocalFilterIcon} from '../../cells/header/cellsHeaderFilters';
import {isLocationHint, VisualSearchHint, VisualSearchHintValue} from './searchHintsTypes';

/*
 * Props.
 */

interface SearchHintItemDropdownProps<T extends VisualSearchHint> extends PopoverTransferProps {
  hint: T;
  onItemClick: (arg: number) => void;
}

/*
 * Styles.
 */

const StyledDropdownRowDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 4px;
`;

const StyledIconWrapperDiv = styled.div`
  grid-area: left-icon;

  width: 16px;
  margin-right: 8px;

  align-self: center;
  display: flex;
  justify-content: center;
`;

/*
 * Component.
 */

export const SearchHintItemDropdown = <T extends VisualSearchHint>(props: SearchHintItemDropdownProps<T>) => {
  const {hint, onItemClick, ...popoverProps} = props;
  const {values} = hint;

  const isSingleChoice = isLocationHint(hint);

  return (
    <SimpleDropdown {...popoverProps}>
      {values.map((searchHintValue, index) => {
        const {title, icon, isSelected, key} = searchHintValue;

        return (
          <DropdownItem
            key={key}
            isChecked={isSelected}
            onSelect={() => onItemClick(index)}
            renderRightContent={
              isSingleChoice ? undefined : () => <DropdownItemCheckbox isChecked={isSelected} />
            }
            shouldReserveRightIcon={isSingleChoice}
          >
            {maybeRenderHintDropdownItemIcon(icon)}
            <DropdownItemContent isChecked={isSelected}>
              <StyledDropdownRowDiv>
                {title}
                {maybeRenderTooltip(searchHintValue)}
              </StyledDropdownRowDiv>
            </DropdownItemContent>
          </DropdownItem>
        );
      })}
    </SimpleDropdown>
  );
};

/*
 * Helpers.
 */

function maybeRenderHintDropdownItemIcon(icon: SearchLocalFilterIconType | undefined) {
  if (!icon) {
    return null;
  }

  return <StyledIconWrapperDiv>{renderLocalFilterIcon(icon)}</StyledIconWrapperDiv>;
}

function maybeRenderTooltip(value: VisualSearchHintValue) {
  if (!('tooltip' in value) || !value.tooltip) {
    return null;
  }

  const {tooltip} = value;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipCoordinator render={() => tooltip}>
          <TooltipIconWithHover color={theme.greys.shade70} />
        </StyledTooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
}
